import { useState } from 'react';
import { DownloadSimple, Tag } from 'shared/icons';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from '../zplPrinting';

/**
 * ETIQUETA DO KIT (regra 10 do contrato do M6): produto + LT + quantidade.
 *
 * O ZPL vem PRONTO na resposta do apontamento (`kit_label.zpl`), gerado pelo
 * mesmo `zplTemplates.js` do M1/M3 — não existe um segundo gerador de etiqueta
 * no sistema, e este componente não monta nada: ele só ESCOLHE a impressora e
 * manda. O caminho de envio também é o único do módulo (`zplPrinting`):
 * impressora de rede cadastrada (via backend, TCP 9100) ou "USB desta máquina"
 * pelo QZ Tray.
 *
 * A prévia mostra o que a etiqueta vai dizer — o operador confere o LOTE antes
 * de colar 200 delas em caixa errada.
 */
const KitLabelPanel = ({ label, kitLabel, fileName = 'kit' }) => {
    const toast = useToast();
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(open);

    const zpl = kitLabel?.zpl ?? null;
    const preview = kitLabel?.preview ?? null;

    const handlePrint = async () => {
        if (!zpl || !selectedPrinter) {
            return;
        }
        setSending(true);
        try {
            const printerName = await SendZplToPrinter({ target: selectedPrinter, printers, zpl, label });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            toast.error(error?.message || label('wms.labels.qz_not_running'));
        } finally {
            setSending(false);
        }
    };

    if (!zpl) {
        return null;
    }

    return (
        <section className="space-y-3 rounded-xl border-2 border-mono-300 bg-white p-4 dark:border-mono-700 dark:bg-mono-950">
            <div className="flex items-center gap-2">
                <Tag size={20} className="text-mono-600 dark:text-mono-300" />
                <h3 className="text-base font-bold text-black dark:text-white">{label('wms.bench.kit_label')}</h3>
            </div>

            {preview ? (
                <div className="rounded-lg border border-dashed border-mono-300 p-3 text-center dark:border-mono-700">
                    <Mono className="block break-all text-xl font-black text-black dark:text-white">
                        {preview.title}
                    </Mono>
                    {(preview.lines ?? []).map((line) => (
                        <span key={line} className="block text-sm text-mono-600 dark:text-mono-400">
                            {line}
                        </span>
                    ))}
                </div>
            ) : null}

            <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.bench.kit_label_hint')}</p>

            {!open ? (
                <Button type="button" variant="secondary" size="lg" className="w-full" onClick={() => setOpen(true)}>
                    <Tag size={20} className="mr-2" />
                    {label('wms.action.print_kit_label')}
                </Button>
            ) : (
                <>
                    <ZplPrinterSelect
                        label={label}
                        printers={printers}
                        localPrinters={localPrinters}
                        value={selectedPrinter}
                        onChange={setSelectedPrinter}
                        disabled={sending}
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            size="lg"
                            className="flex-1"
                            onClick={handlePrint}
                            loading={sending}
                            disabled={!selectedPrinter}
                        >
                            <Tag size={18} className="mr-2" />
                            {label('wms.labels.send')}
                        </Button>
                        <Button
                            type="button"
                            size="lg"
                            variant="secondary"
                            disabled={sending}
                            onClick={() => DownloadZplFile(zpl, `kit-${fileName}.zpl`)}
                        >
                            <DownloadSimple size={18} className="mr-2" />
                            {label('wms.labels.download')}
                        </Button>
                    </div>
                </>
            )}
        </section>
    );
};

export default KitLabelPanel;
